import { useState } from "react";
import { DateTime } from "luxon";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Step,
  StepButton,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { type FieldPath, useFormContext, useWatch } from "react-hook-form";
import {
  BooleanInput,
  DateInput,
  NumberInput,
  RecordContextProvider,
  SelectInput,
  TextInput,
  required,
  useGetList,
  useCreate,
  useRefresh,
} from "react-admin";
import { currentUserRoles, isAutovioStaff } from "../../../backoffice.access_control";
import { useCurrentDrivingSchoolId } from "../../../hooks/useCurrentDrivingSchoolId";
import { Form2 } from "../../../misc/Form2";
import { SaveButton2 } from "../../../buttons/SaveButton2";
import { Column } from "../../../misc/Column";
import { Row } from "../../../misc/Row";
import { LoadingIndicator } from "../../../misc/LoadingIndicator";
import type { DATEVConfigRecord } from "../../../providers/datevProvider";
import { useDATEVValidationNavigation } from "../../../hooks/useDATEVValidationNavigation";
import {
  DATEV_ACCOUNT_DESCRIPTIONS,
  DATEV_ACCOUNT_GROUPS,
  DATEV_PREPAID_EXPORT_LABELS,
  getDATEVAccountLabel,
} from "./datevLabels";
import { DATEVPrepaidSettings } from "./DATEVPrepaidSettings";

export function DATEVSettings() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { data, isLoading, error } = useGetList<DATEVConfigRecord>("datevConfigs", {
    filter: { drivingSchoolId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "valid_from", order: "DESC" },
  });
  const [selectedId, setSelectedId] = useState<string>();
  if (isLoading) return <LoadingIndicator />;
  if (error) return <Alert severity="error">Die DATEV-Einstellungen konnten nicht geladen werden.</Alert>;
  if (!data?.length)
    return (
      <Alert severity="info">
        Für diese Fahrschule ist noch keine DATEV-Konfiguration eingerichtet. Bitte wende dich an deine
        Partnerbetreuung.
      </Alert>
    );
  const config = data.find((c) => c.id === selectedId) ?? data[0];
  return (
    <Column>
      {data.length > 1 && (
        <TextField
          select
          label="Konfigurationsversion"
          value={config.id}
          onChange={(event) => setSelectedId(event.target.value)}
        >
          {data.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.skr}
              {hasMeaningfulValidFrom(c) ? ` · gültig ab ${new Date(c.valid_from!).toLocaleDateString("de-DE")}` : ""}
              {c.valid_until ? ` bis ${new Date(c.valid_until).toLocaleDateString("de-DE")}` : ""}
            </MenuItem>
          ))}
        </TextField>
      )}
      <DATEVSettingsForm
        key={`${config.id}:${config.updated_at}`}
        config={config}
        onCreated={(id) => setSelectedId(id)}
      />
    </Column>
  );
}

function DATEVSettingsForm(props: { config: DATEVConfigRecord; onCreated: (id: string) => void }) {
  const { config, onCreated } = props;
  const [create] = useCreate<DATEVConfigRecord>();
  const refresh = useRefresh();
  const [activeStep, setActiveStep] = useState(0);
  async function save(data: DATEVConfigRecord) {
    await create(
      "datevConfigs",
      { data: { ...data, id: config.id } },
      {
        returnPromise: true,
        onSuccess: (created) => {
          onCreated(created.id);
          refresh();
          showSuccessToast("Neue DATEV-Konfigurationsversion erstellt.");
        },
        onError: (error) => handleError("DATEV-Einstellungen konnten nicht gespeichert werden.", error),
      },
    );
  }
  return (
    <RecordContextProvider value={{ ...config, change_reason: "" }}>
      <Form2<DATEVConfigRecord>
        onSubmit={async (data) => {
          if (config.can_edit && activeStep === 4) await save(data);
        }}
        defaultValues={{
          ...config,
          omit_service_dates: config.omit_service_dates ?? false,
          change_reason: "",
          effective_on: earliestVersionDate(config),
          account_numbers: Object.fromEntries(config.account_mappings.map((m) => [m.id, m.account_number])),
        }}
      >
        <DATEVSettingsWizard config={config} activeStep={activeStep} onStepChange={setActiveStep} />
      </Form2>
    </RecordContextProvider>
  );
}

function earliestVersionDate(config: DATEVConfigRecord) {
  const today = DateTime.now().setZone("Europe/Berlin").startOf("day");
  const start = DateTime.fromISO(config.valid_from ?? config.created_at, { zone: "Europe/Berlin" });
  return (start >= today ? start.plus({ days: 1 }).startOf("day") : today).toISODate();
}

function hasMeaningfulValidFrom(config: DATEVConfigRecord) {
  return !!config.valid_from && !config.valid_from.startsWith("1970-01-01");
}

const STUDENT_ACCOUNT_ROLES = ["PREPAID_LIABILITY", "PREPAID_CLEARING"];

const DATEV_SETTLEMENT_DESCRIPTIONS: Record<string, string> = {
  PAYOUT_ALLOCATION: "Zahlungen werden den jeweiligen Auszahlungen zugeordnet und im Auszahlungsstapel gebucht.",
  PAYMENT_LEDGER:
    "Schülerzahlungen werden zum Zahlungsdatum gebucht. Auszahlungen werden separat zwischen Zahlungsanbieter- und Auszahlungsverrechnungskonto gebucht.",
  PAYMENT_LEDGER_SINGLE:
    "Zahlungen und AUTOVIO-Gebühren laufen über dasselbe Verrechnungskonto. Zahlungsstapel nach Zahlungsdatum und Auszahlungsstapel sind alternative Wege für dieselben Zahlungen.",
};

const DATEV_STEPS = ["Steuerbüro", "Schülerkonten", "Kontenzuordnung", "Exportoptionen", "Prüfen"];

function DATEVSettingsWizard(props: {
  config: DATEVConfigRecord;
  activeStep: number;
  onStepChange: (step: number) => void;
}) {
  const { config, activeStep, onStepChange } = props;
  const disabled = !config.can_edit;
  const { trigger, getValues, setValue } = useFormContext<DATEVConfigRecord>();
  const debtorMode = useWatch({ name: "debtor_mode" });
  const simplifiedBookingText = useWatch({ name: "simplified_buchungstext" });
  const settlementMode = useWatch({ name: "settlement_mode" });
  const paymentScope = useWatch({ name: "payment_scope" });
  const showPrepaidAccounts =
    config.prepaid_export_mode !== "SERVICES_ONLY" && config.prepaid_accounting_mode !== "DEBTOR_CREDIT";
  const fieldsByStep = [
    ["consultant_number", "client_number", "fiscal_year_start_month", "fiscal_year_start_day"],
    [
      "debtor_mode",
      "collective_debtor_number",
      "individual_debtor_start",
      "individual_debtor_end",
      "debitor_adressattyp",
      ...config.account_mappings
        .filter((m) => showPrepaidAccounts && STUDENT_ACCOUNT_ROLES.includes(m.account_role))
        .map((m) => `account_numbers.${m.id}`),
    ],
    config.account_mappings
      .filter((m) => !STUDENT_ACCOUNT_ROLES.includes(m.account_role))
      .map((m) => `account_numbers.${m.id}`),
    [
      "settlement_mode",
      "payment_scope",
      "customer_reference_field",
      "include_cash_payments",
      "include_bank_transfer_payments",
      "buchungstext_format",
      "simplified_buchungstext",
      "omit_service_dates",
      "festschreibung",
    ],
  ];
  useDATEVValidationNavigation(fieldsByStep, onStepChange);
  async function goTo(step: number) {
    if (
      step < activeStep ||
      disabled ||
      (await trigger(fieldsByStep[activeStep] as FieldPath<DATEVConfigRecord>[], { shouldFocus: true }))
    ) {
      onStepChange(step);
    }
  }
  return (
    <Column spacing={3} sx={{ maxWidth: 900 }}>
      <Typography variant="body2">
        {disabled
          ? "Wähle einen Bereich, um die DATEV-Einstellungen anzusehen."
          : "Du kannst direkt zwischen den Bereichen wechseln. Prüfe deine Änderungen vor dem Speichern."}
      </Typography>
      {disabled && (
        <Alert severity="info">
          Du kannst diese Einstellungen ansehen. Änderungen benötigen eine besondere Berechtigung.
        </Alert>
      )}
      <Stepper nonLinear activeStep={activeStep} alternativeLabel sx={{ display: { xs: "none", sm: "flex" } }}>
        {DATEV_STEPS.map((label, index) => (
          <Step key={label} completed={false}>
            <StepButton onClick={() => onStepChange(index)}>{label}</StepButton>
          </Step>
        ))}
      </Stepper>
      <TextField
        select
        label="Bereich"
        value={activeStep}
        onChange={(event) => onStepChange(Number(event.target.value))}
        sx={{ display: { xs: "flex", sm: "none" } }}
      >
        {DATEV_STEPS.map((label, index) => (
          <MenuItem key={label} value={index}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="h6">
        Schritt {activeStep + 1} von {DATEV_STEPS.length}: {DATEV_STEPS[activeStep]}
      </Typography>
      <Box hidden={activeStep !== 0}>
        <Column spacing={2}>
          <Typography variant="body2">Diese Nummern erhältst du von deinem Steuerbüro.</Typography>
          <Row>
            <NumberInput
              source="consultant_number"
              label="Beraternummer"
              disabled={disabled}
              min={1001}
              max={9999999}
              validate={required("Bitte gib die Beraternummer ein.")}
            />
            <NumberInput
              source="client_number"
              label="Mandantennummer"
              disabled={disabled}
              min={1}
              max={99999}
              validate={required("Bitte gib die Mandantennummer ein.")}
            />
          </Row>
          <Typography variant="body2">
            Kontenrahmen: {config.skr}
            {hasMeaningfulValidFrom(config)
              ? ` · Gültig ab ${new Date(config.valid_from!).toLocaleDateString("de-DE")}`
              : ""}
          </Typography>
          <Row>
            <NumberInput
              source="fiscal_year_start_month"
              label="Wirtschaftsjahresbeginn: Monat"
              min={1}
              max={12}
              disabled={disabled}
            />
            <NumberInput source="fiscal_year_start_day" label="Tag" min={1} max={31} disabled={disabled} />
          </Row>
        </Column>
      </Box>
      <Box hidden={activeStep !== 1}>
        <Column spacing={2}>
          <Typography variant="body2">
            Lege fest, wie Schüler und ihre Guthaben in der Buchhaltung geführt werden.
          </Typography>
          <SelectInput
            source="debtor_mode"
            label="Schülerkonten"
            disabled={disabled}
            validate={required("Bitte wähle, wie die Schülerkonten geführt werden.")}
            choices={[
              { id: "COLLECTIVE", name: "Ein gemeinsames Konto für alle Schüler" },
              { id: "INDIVIDUAL", name: "Ein eigenes Konto je Schüler" },
            ]}
          />
          <Box hidden={debtorMode !== "COLLECTIVE"}>
            <TextInput source="collective_debtor_number" label="Sammeldebitor" disabled={disabled} />
          </Box>
          <Box hidden={debtorMode !== "INDIVIDUAL"}>
            <Row>
              <NumberInput source="individual_debtor_start" label="Erste Schülerkontonummer" disabled={disabled} />
              <NumberInput source="individual_debtor_end" label="Letzte Schülerkontonummer" disabled={disabled} />
            </Row>
          </Box>
          <SelectInput
            source="debitor_adressattyp"
            label="Adressattyp im Debitorenstamm"
            disabled={disabled}
            emptyText="Bisherigen Adressattyp beibehalten"
            emptyValue=""
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            format={(value) => value ?? ""}
            parse={(value) => value || null}
            choices={[
              { id: "0", name: "0 – Keine Angabe" },
              { id: "1", name: "1 – Natürliche Person" },
              { id: "2", name: "2 – Unternehmen" },
            ]}
            helperText="Gilt für die Debitorenstammdaten im neuen DATEV-Export. Ohne Auswahl bleibt die bisherige Einstellung erhalten."
          />
          <DATEVPrepaidSettings config={config} />
          {showPrepaidAccounts && <DATEVAccountFields config={config} disabled={disabled} section="student" />}
        </Column>
      </Box>
      <Box hidden={activeStep !== 2}>
        <Column spacing={2}>
          <Typography variant="body2">Übernimm die Kontonummern aus dem Kontenplan deines Steuerbüros.</Typography>
          <DATEVAccountFields config={config} disabled={disabled} />
        </Column>
      </Box>
      <Box hidden={activeStep !== 3}>
        <Column spacing={2}>
          <Typography variant="body2">
            Stimme den Zahlungsabgleich und den Umfang der Exporte mit deinem Steuerbüro ab.
          </Typography>
          <SelectInput
            source="settlement_mode"
            label="Zahlungsabgleich"
            disabled={disabled}
            validate={required("Bitte wähle die Art des Zahlungsabgleichs.")}
            helperText={DATEV_SETTLEMENT_DESCRIPTIONS[settlementMode]}
            choices={[
              { id: "PAYOUT_ALLOCATION", name: "Zahlungen nach Auszahlung zuordnen" },
              { id: "PAYMENT_LEDGER", name: "Zahlungen und Auszahlungen getrennt buchen" },
              { id: "PAYMENT_LEDGER_SINGLE", name: "Zahlungen auf einem gemeinsamen Verrechnungskonto" },
            ]}
          />
          <SelectInput
            source="payment_scope"
            label="Umfang des Zahlungsstapels"
            disabled={disabled}
            validate={required("Bitte wähle den Umfang des Zahlungsstapels.")}
            helperText={
              paymentScope === "EXTERNAL_ONLY"
                ? "Exportiert ausschließlich die unten aktivierten Barzahlungen und direkten Banküberweisungen. Stripe-Zahlungen bleiben im Auszahlungsstapel."
                : "Exportiert Zahlungen über den Zahlungsanbieter sowie die unten aktivierten Barzahlungen und direkten Banküberweisungen."
            }
            choices={[
              { id: "ALL", name: "Alle aktivierten Zahlungsarten" },
              { id: "EXTERNAL_ONLY", name: "Nur Barzahlungen und direkte Überweisungen" },
            ]}
          />
          <BooleanInput
            source="include_cash_payments"
            label="Barzahlungen im Zahlungsstapel berücksichtigen"
            disabled={disabled}
            helperText="An: Barzahlungen werden gegen das Kassenkonto gebucht. Aus: Der DATEV-Export enthält diese Zahlungen nicht; sie müssen anderweitig gebucht werden."
          />
          <BooleanInput
            source="include_bank_transfer_payments"
            label="Direkte Banküberweisungen berücksichtigen"
            disabled={disabled}
            helperText="An: Direkte Schülerüberweisungen werden gegen das Bankkonto gebucht. Aus: Diese Zahlungen bleiben außerhalb des Exports. Stripe-Auszahlungen sind damit nicht gemeint."
          />
          <SelectInput
            source="customer_reference_field"
            label="Auftragsnummer im Export"
            defaultValue="AUFTRAGSNUMMER"
            disabled={disabled}
            validate={required("Bitte wähle, ob eine Auftragsnummer ausgegeben werden soll.")}
            choices={[
              { id: "AUFTRAGSNUMMER", name: "Kundennummer als Auftragsnummer ausgeben" },
              { id: "NONE", name: "Keine Auftragsnummer ausgeben" },
              ...(config.customer_reference_field === "BELEGFELD2"
                ? [{ id: "BELEGFELD2", name: "Belegfeld 2 (bestehende Einstellung)" }]
                : []),
            ]}
            helperText="Ohne Auftragsnummer bleibt die Spalte leer. Belegnummer und Schülerkonto bleiben erhalten."
          />
          <BooleanInput
            source="simplified_buchungstext"
            label="Vereinfachte Buchungstexte"
            disabled={disabled}
            onChange={(event) => {
              if (event.target.checked) setValue("buchungstext_format", "", { shouldDirty: true });
            }}
            helperText="Verwendet den Empfängernamen bzw. einen kurzen AUTOVIO-Text. Eine vorhandene Vorlage wird beim Aktivieren entfernt."
          />
          <Box hidden={simplifiedBookingText}>
            <Column spacing={1}>
              <TextInput
                source="buchungstext_format"
                label="Vorlage für den Buchungstext"
                disabled={disabled}
                fullWidth
                helperText="Leer lassen für den Standardtext. Der Export kürzt den Buchungstext auf 60 Zeichen."
              />
              <Typography variant="body2" color="text.secondary">
                {
                  "{description}: Beschreibung · {recipient_full_name}: vollständiger Empfängername · {recipient_first_name}: Vorname · {recipient_last_name}: Nachname · {calculation_type}: Belegart"
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {"Beispiel: {recipient_last_name}, {recipient_first_name} - {description}"}
              </Typography>
            </Column>
          </Box>
          <BooleanInput
            source="omit_service_dates"
            label="Leistungsdatum im gesamten Export weglassen"
            disabled={disabled}
            helperText="Gilt nur für den neuen Export: Leistungsdatum und das daraus abgeleitete Steuerperiodendatum bleiben für alle Konten leer. Aus: bisheriges Verhalten."
          />
          <BooleanInput source="festschreibung" label="Stapel als festgeschrieben exportieren" disabled={disabled} />
        </Column>
      </Box>
      {activeStep === 4 && (
        <Column spacing={2}>
          <DATEVSettingsReview config={config} values={getValues()} />
          <Typography variant="body2">
            Beim Speichern entsteht eine neue Konfigurationsversion. Bis zum gewählten Beginn gilt die bisherige
            Version, danach die neue. Bereits erstellte Exportdateien bleiben unverändert.
          </Typography>
          {!disabled && (
            <Column spacing={2}>
              <DateInput
                source="effective_on"
                label="Neue Version gültig ab"
                inputProps={{ min: earliestVersionDate(config) }}
                validate={[
                  required("Bitte wähle den Gültigkeitsbeginn."),
                  (value) =>
                    value && value < earliestVersionDate(config)
                      ? `Der früheste Versionsbeginn ist ${new Date(earliestVersionDate(config)).toLocaleDateString("de-DE")}.`
                      : undefined,
                ]}
                helperText="Gilt ab Tagesbeginn in Deutschland. Frühere Zeiträume verwenden weiterhin die bisherige Version."
              />
              <TextInput
                source="change_reason"
                label="Grund der Änderung"
                helperText="Bitte beschreibe den Anlass dieser Änderung. Die Begründung der vorherigen Änderung wird nicht übernommen."
                multiline
                validate={required("Bitte begründe die Änderung für die Nachvollziehbarkeit.")}
              />
            </Column>
          )}
        </Column>
      )}
      <Row gap={2}>
        <Button type="button" disabled={activeStep === 0} onClick={() => onStepChange(activeStep - 1)}>
          Zurück
        </Button>
        {activeStep < 4 ? (
          <Button type="button" variant="contained" onClick={() => goTo(activeStep + 1)}>
            Weiter
          </Button>
        ) : (
          !disabled && <SaveButton2 label="Neue Version speichern" />
        )}
      </Row>
    </Column>
  );
}

function DATEVSettingsReview(props: { config: DATEVConfigRecord; values: DATEVConfigRecord }) {
  const { config } = props;
  return (
    <Column spacing={2}>
      <Typography variant="body2">
        Guthabenbuchungen im Monatsabschluss: {DATEV_PREPAID_EXPORT_LABELS[config.prepaid_export_mode ?? "FULL"]}
      </Typography>
      <DATEVSettingsChanges {...props} />
    </Column>
  );
}

function DATEVSettingsChanges(props: { config: DATEVConfigRecord; values: DATEVConfigRecord }) {
  const { config, values } = props;
  const labels: Record<string, string> = {
    consultant_number: "Beraternummer",
    client_number: "Mandantennummer",
    fiscal_year_start_month: "Wirtschaftsjahresbeginn: Monat",
    fiscal_year_start_day: "Wirtschaftsjahresbeginn: Tag",
    debtor_mode: "Schülerkonten",
    collective_debtor_number: "Sammeldebitor",
    individual_debtor_start: "Erste Schülerkontonummer",
    individual_debtor_end: "Letzte Schülerkontonummer",
    debitor_adressattyp: "Adressattyp im Debitorenstamm",
    prepaid_accounting_mode: "Schülerguthaben",
    settlement_mode: "Zahlungsabgleich",
    payment_scope: "Umfang des Zahlungsstapels",
    customer_reference_field: "Auftragsnummer im Export",
    include_cash_payments: "Barzahlungen berücksichtigen",
    include_bank_transfer_payments: "Direkte Überweisungen berücksichtigen",
    buchungstext_format: "Buchungstext",
    simplified_buchungstext: "Vereinfachte Buchungstexte",
    omit_service_dates: "Leistungsdatum im gesamten Export weglassen",
    festschreibung: "Festschreibung",
  };
  const valueLabels: Record<string, string> = {
    COLLECTIVE: "Gemeinsames Schülerkonto",
    INDIVIDUAL: "Eigenes Konto je Schüler",
    LIABILITY: "Separates Guthabenkonto",
    DEBTOR_CREDIT: "Guthaben auf dem Schülerkonto",
    PAYOUT_ALLOCATION: "Nach Auszahlung zuordnen",
    PAYMENT_LEDGER: "Zahlungen und Auszahlungen getrennt buchen",
    PAYMENT_LEDGER_SINGLE: "Gemeinsames Verrechnungskonto",
    ALL: "Alle aktivierten Zahlungsarten",
    EXTERNAL_ONLY: "Barzahlungen und direkte Überweisungen",
    AUFTRAGSNUMMER: "Kundennummer als Auftragsnummer ausgeben",
    BELEGFELD2: "Belegfeld 2",
    NONE: "Keine Auftragsnummer ausgeben",
  };
  function display(value: unknown, field: string) {
    if (field === "debitor_adressattyp") {
      const addressTypes: Record<string, string> = {
        "0": "0 – Keine Angabe",
        "1": "1 – Natürliche Person",
        "2": "2 – Unternehmen",
      };
      return addressTypes[String(value)] ?? "Bisherigen Adressattyp beibehalten";
    }
    if (typeof value === "boolean") return value ? "Ja" : "Nein";
    if (typeof value !== "string" && typeof value !== "number") return "Nicht angegeben";
    return valueLabels[String(value)] ?? (value === "" ? "Nicht angegeben" : String(value));
  }
  const changes = Object.entries(labels)
    .map(([field, label]) => ({
      field,
      label,
      before:
        field === "omit_service_dates"
          ? (config.omit_service_dates ?? false)
          : config[field as keyof DATEVConfigRecord],
      after: values[field as keyof DATEVConfigRecord],
    }))
    .filter((change) => change.before !== change.after);
  for (const mapping of config.account_mappings) {
    const after = values.account_numbers?.[mapping.id];
    if (after !== mapping.account_number)
      changes.push({
        field: `account_numbers.${mapping.id}`,
        label: getDATEVAccountLabel(mapping.account_role),
        before: mapping.account_number,
        after,
      });
  }
  if (!config.can_edit)
    return (
      <Alert severity="info">
        Du hast alle Einstellungen angesehen. Zum Bearbeiten benötigst du eine besondere Berechtigung.
      </Alert>
    );
  if (!changes.length) return <Alert severity="info">Du hast noch keine Einstellungen geändert.</Alert>;
  return (
    <Column spacing={1}>
      {changes.map((change, index) => (
        <Box key={index}>
          <Typography variant="subtitle2">{change.label}</Typography>
          <Typography variant="body2">
            {display(change.before, change.field)} → {display(change.after, change.field)}
          </Typography>
        </Box>
      ))}
    </Column>
  );
}

function DATEVAccountFields(props: { config: DATEVConfigRecord; disabled: boolean; section?: "student" | "accounts" }) {
  const { config, disabled, section = "accounts" } = props;
  const prepaidMode = useWatch({ name: "prepaid_accounting_mode" });
  const canViewInternalAccounts = isAutovioStaff(currentUserRoles);
  const knownRoles = new Set(DATEV_ACCOUNT_GROUPS.flatMap((group) => group.roles));
  const groups = [
    ...DATEV_ACCOUNT_GROUPS,
    {
      label: "Weitere Konten",
      description: "Zusätzliche Zuordnungen aus deinem Kontenplan.",
      roles: config.account_mappings
        .filter((mapping) => !knownRoles.has(mapping.account_role))
        .map((mapping) => mapping.account_role),
    },
  ];
  return (
    <Column spacing={2}>
      {groups.map((group) => {
        const mappings = group.roles.flatMap((role) =>
          config.account_mappings.filter(
            (mapping) =>
              mapping.account_role === role &&
              STUDENT_ACCOUNT_ROLES.includes(role) === (section === "student") &&
              (role !== "OPENING_BALANCE" || canViewInternalAccounts),
          ),
        );
        if (!mappings.length) return null;
        return (
          <Card key={group.label} variant="outlined" sx={{ boxShadow: "none" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>
                {group.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {group.description}
              </Typography>
              <Grid container spacing={2}>
                {mappings.map((mapping) => (
                  <Grid item xs={12} sm={6} key={mapping.id}>
                    <TextInput
                      source={`account_numbers.${mapping.id}`}
                      label={
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          {getDATEVAccountLabel(mapping.account_role)}
                          <Tooltip
                            title={
                              DATEV_ACCOUNT_DESCRIPTIONS[mapping.account_role] ??
                              "Zusätzliches Konto gemäß den hinterlegten Buchungsregeln dieser DATEV-Konfiguration."
                            }
                            arrow
                          >
                            <IconButton
                              type="button"
                              size="small"
                              aria-label={`Information zu ${getDATEVAccountLabel(mapping.account_role)}`}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      disabled={disabled}
                      validate={
                        section !== "student" || prepaidMode === "LIABILITY"
                          ? required("Bitte gib eine Kontonummer ein.")
                          : undefined
                      }
                      fullWidth
                      size="small"
                      helperText={false}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Column>
  );
}
